import { Flex, FlexProps } from "@chakra-ui/layout";
import { Collapse, Progress, Spinner, Text } from "@chakra-ui/react";
import useTasks from "core/features/task/hooks/useTasks";
import { TaskResponse, TaskTypeEnum } from "core/features/task/typings";
import colors from "core/resources/theme/colors";
import React, { useState, useEffect } from "react";
import { AiOutlineClose, AiOutlineMinus } from "react-icons/ai";
import { MdCheckCircleOutline, MdErrorOutline } from "react-icons/md";

interface DrawerProcessamentoProps {
  isOpen: boolean;
  onClose: () => any;
  refetchSignal?: number;
  localTasks?: TaskResponse[];
}

const IconWrapper = ({
  children,
  ...rest
}: { children: React.ReactNode } & FlexProps) => (
  <Flex
    cursor="pointer"
    rounded="full"
    borderWidth={2}
    borderColor={colors.white}
    w="24px"
    h="24px"
    alignItems="center"
    justifyContent="center"
    {...rest}
  >
    {children}
  </Flex>
);

export type WindowState = "minimized" | "drawer";

const transition: Partial<FlexProps> = {
  transition: "all",
  transitionDuration: "0.25s",
  transitionTimingFunction: "ease-in-out",
};

function DrawerProcessamento({
  isOpen,
  onClose,
  refetchSignal,
  localTasks = [],
}: DrawerProcessamentoProps) {
  const [previousState, setPreviousState] = useState<WindowState>("drawer");
  const [windowState, setWindowState] = useState<WindowState>("drawer");

  const { isFetching, data, refetch } = useTasks(isOpen);

  useEffect(() => {
    if (refetchSignal) {
      setWindowState("drawer");
      refetch();
    }
  }, [refetch, refetchSignal]);

  const allTasks = React.useMemo(() => {
    const fetchedTasks = data || [];
    return [...localTasks, ...fetchedTasks].sort(
      (a, b) => b.createdAt - a.createdAt
    );
  }, [localTasks, data]);

  const onMinimize = () => {
    const prevState = previousState;
    setWindowState((state) => {
      if (state !== "minimized") {
        setPreviousState(state);
        return "minimized";
      }
      return prevState;
    });
  };

  const getTaskStates = (
    task: TaskResponse
  ): {
    error: boolean;
    success: boolean;
    loading: boolean;
  } => {
    if (task.type === TaskTypeEnum.ProcessarArrecadacao) {
      return {
        error: !!task.finishDate && !!task.errors?.length,
        success: !!task.finishDate && !task.errors?.length,
        loading: !task.finishDate,
      };
    }

    return {
      error: false,
      success: false,
      loading: false,
    };
  };

  return (
    <>
      <Flex
        position="fixed"
        left={"80px"}
        bottom={0}
        rounded={8}
        shadow="lg"
        flexDir="column"
        zIndex={9999}
        {...transition}
      >
        <Flex
          bg={colors.primary}
          roundedTop={8}
          p={4}
          w="100vw"
          maxW={"450px"}
          alignItems="center"
          {...transition}
        >
          <Text color={colors.white} fontSize={16} fontWeight="medium" mr={4}>
            {allTasks?.filter((task) => !task.finishDate)?.length
              ? `(${allTasks?.filter((task) => !task.finishDate).length})`
              : null}{" "}
            Processamentos
          </Text>
          {isFetching ? <Spinner size="sm" color={colors.white} /> : null}

          <IconWrapper
            ml="auto"
            onClick={onMinimize}
            alignItems={windowState === "minimized" ? "flex-start" : "flex-end"}
          >
            <AiOutlineMinus color={colors.white} fontSize={16} />
          </IconWrapper>

          <IconWrapper ml={2} onClick={onClose}>
            <AiOutlineClose color={colors.white} fontSize={16} />
          </IconWrapper>
        </Flex>
        <Collapse in={windowState !== "minimized"}>
          <Flex
            w="100vw"
            maxW={"450px"}
            h="75vh"
            maxH={"400px"}
            flexDir="column"
            bg={colors.white}
            overflow="auto"
            {...transition}
          >
            <Flex flexDir="column" p={4} gridGap={2}>
              {isFetching ? (
                <Progress
                  size="sm"
                  isIndeterminate
                  w="100%"
                  colorScheme="cyan"
                />
              ) : null}

              {allTasks?.map((task) => {
                const { error, success, loading } = getTaskStates(task);

                return (
                  <Flex
                    gap={1}
                    alignItems="center"
                    key={task.id}
                    w="100%"
                    justifyContent="space-between"
                    pb={4}
                    borderBottomWidth={1}
                    borderColor={colors.black100}
                  >
                    <Flex flexDir="column" gridGap={0.5}>
                      <Text fontWeight="bold">{task.title}</Text>
                      <Text fontSize="xs" color={colors.black500}>
                        Criado em: {new Date(task.createdAt).toLocaleString()}
                      </Text>
                      <Text fontSize="xs" color={colors.black500}>
                        Finalizado em:{" "}
                        {task.finishDate
                          ? new Date(task.finishDate).toLocaleString()
                          : "Não finalizado"}
                      </Text>
                      {task.errors?.length ? (
                        <Text fontSize="sm" color={colors.accentNegative}>
                          Erros:{" "}
                          {task.errors.map((error) => error.message).join(", ")}
                        </Text>
                      ) : null}
                    </Flex>

                    {loading ? (
                      <Spinner size="lg" color={colors.primary} />
                    ) : null}

                    {!loading && error ? (
                      <MdErrorOutline size={48} color={colors.accentNegative} />
                    ) : null}

                    {!loading && success ? (
                      <MdCheckCircleOutline
                        size={48}
                        color={colors.accentPositive}
                      />
                    ) : null}
                  </Flex>
                );
              })}
            </Flex>
          </Flex>
        </Collapse>
      </Flex>
    </>
  );
}

export default DrawerProcessamento;
